<template>
  <div class="sa-detail" v-if="report">
    <!-- Detail navigation and request actions -->
    <div class="sa-detail-toolbar">
      <div v-if="canCancelTicket(report)">
        <button
          type="button"
          class="btn btn-outline-danger cancel-ticket-button font-weight-bold"
          @click="cancelTicket"
          :disabled="isProcessing"
        >
          <CIcon name="cil-ban" size="sm" class="mr-1" />
          {{ $t('table.user.btn.cancelRequest') }}
        </button>
      </div>
      <button type="button" class="btn sa-detail-back" @click="$emit('close')">
        {{ text('กลับหน้ารายการ', 'Back to requests') }}
      </button>
    </div>

    <!-- Hero Banner -->
    <section class="sa-detail-hero">
      <div>
        <p>{{ text('งานแจ้งซ่อม', 'Repair request') }}</p>
        <h1>{{ report.ticketNumber }}</h1>
        <span>{{ getUserName() }} • {{ getReporterAffiliation() }}</span>
      </div>
      <div class="sa-current-status">
        <strong :class="{ 'status-cancelled': report.status === 'cancelled' }">{{ statusLabel(report.status) }}</strong>
        <p>{{ nextStep }}</p>
        <small>{{ text('ข้อมูลล่าสุด', 'Updated') }} {{ formatDate(report.updatedAt || report.createdAt) }}</small>
      </div>
    </section>

    <!-- Panel 1: ความคืบหน้าการซ่อม & ข้อมูลตามขั้นตอน -->
    <section class="sa-detail-panel">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 10px;">
        <h2 class="m-0">{{ text('ความคืบหน้าการซ่อม', 'Repair progress') }}</h2>
      </div>

      <ol class="sa-timeline">
        <li
          v-for="(step, index) in timeline"
          :key="step.status"
          :class="{
            reached: step.reached,
            current: step.status === report.status,
            'active-step': activeStep === step.status,
            'disabled-step': !step.reached
          }"
          :aria-current="step.status === report.status ? 'step' : null"
          v-keyboard-activate role="button" :tabindex="step.reached ? 0 : -1" :aria-disabled="String(!step.reached)" :aria-pressed="String(activeStep === step.status)"
          @click="step.reached ? selectStep(step.status) : null"
          :title="step.reached ? text('คลิกเพื่อดูขั้นตอนนี้', 'Click to view step') : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached yet')"
        >
          <span class="sa-step-number">{{ index + 1 }}</span>
          <strong>{{ step.label }}</strong>
          <small>{{ step.date ? formatDate(step.date) : (step.reached ? (step.status === 'assigned' && report.status === 'pending' ? text('รอดำเนินการ', 'Pending') : text('ไม่มีเวลาบันทึก', 'Time not recorded')) : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached')) }}</small>
        </li>
      </ol>

      <p v-if="report.status === 'cancelled'" class="alert alert-secondary">
        <strong class="text-danger">{{ text('ยกเลิกงาน:', 'Cancelled:') }}</strong> {{ report.cancelReason || text('ไม่ได้ระบุเหตุผล', 'No reason recorded') }}
      </p>

      <!-- Step Content Container -->
      <div class="sa-step-container">
        <!-- Step 1: แจ้งเรื่อง -->
        <div v-show="(activeStep === 'pending' || activeStep === 'all') && isStepReached('pending')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">1</span>
              {{ text('รายละเอียดการแจ้งซ่อม', 'Report details') }}
            </h3>
            <span class="text-muted small">{{ formatDate(getReportInformedDate()) }}</span>
          </div>

          <TicketDetailSummary
            :ticket-number="report.ticketNumber || '-'"
            :reporter="getUserName()"
            :affiliation="getReporterAffiliation()"
            :section="getSectionName()"
            :personal-phone="getPersonalPhone()"
            :office-phone="getOfficePhone()"
            :job-type="getCategoryName()"
            :work-area="getLocationFull()"
            :time-slot="getAvailableTime()"
            :on-behalf-of="getOnBehalfOf()"
            :technician-phone="getTechnicianPhone()"
          />
          <h4 class="mt-4 mb-2 font-weight-bold" style="font-size: 16px; color: #1e293b;">
            {{ text('รายละเอียดปัญหา', 'Problem description') }}
          </h4>
          <p class="sa-description" v-html="getProblemDetails()"></p>
          <p v-if="getAssetCode()" class="font-weight-bold text-danger mb-3">
            <CIcon name="cil-tag" class="mr-1" /> {{ text('รหัสครุภัณฑ์:', 'Asset code:') }} {{ getAssetCode() }}
          </p>

          <div v-if="isOutdoor()" class="mb-4">
            <label class="lbl font-weight-bold text-dark mb-2">{{ $t('ticketDetail.mapPoint') }}</label>
            <div class="map-container rounded overflow-hidden" style="height: 350px;">
              <div ref="mapContainer" style="width: 100%; height: 100%"></div>
            </div>
            <div class="mt-2 text-right">
              <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                <CIcon name="cil-map" class="mr-1" /> {{ $t('ticketDetail.openMaps') }} ↗
              </a>
            </div>
          </div>

          <div class="mt-3">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพปัญหาที่แจ้ง', 'Reported problem photo') }}</h5>
            <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" @open="openImage" @error="handleImageError" />
            <p v-else class="text-muted small">{{ text('ไม่มีภาพแนบ', 'No image attached') }}</p>
          </div>
        </div>

        <!-- Step 2: รับเรื่อง/มอบหมาย -->
        <div v-show="(activeStep === 'assigned' || activeStep === 'all') && isStepReached('assigned')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">2</span>
              {{ text('การรับเรื่องและมอบหมายงาน', 'Assignment details') }}
            </h3>
            <span class="text-muted small" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
              {{ formatDate(report.supervisorPart.dateTimeInformed) }}
            </span>
          </div>

          <div class="sa-responsibility">
            <div>
              <span>{{ text('ช่างผู้รับผิดชอบ', 'Assigned technicians') }}</span>
              <strong>{{ getTechnicianName() || text('ยังไม่มอบหมาย', 'Unassigned') }}</strong>
            </div>
            <div>
              <span>{{ text('หน่วยงานรับผิดชอบ', 'Maintenance section') }}</span>
              <strong>{{ getSectionName() }}</strong>
            </div>
            <div>
              <span>{{ text('กำหนดซ่อม', 'Repair deadline') }}</span>
              <strong :class="{ 'text-danger': overdue }">{{ report.slaDeadline ? formatDate(report.slaDeadline) : text('ยังไม่ระบุ', 'Not set') }}</strong>
              <small v-if="overdue" class="text-danger d-block mt-1">{{ text('เกินกำหนดซ่อม', 'Overdue') }}</small>
            </div>
            <div>
              <span>{{ text('ระยะเวลาซ่อมโดยประมาณ', 'Estimated repair time') }}</span>
              <strong>{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
            </div>
          </div>
        </div>

        <!-- Step 3: กำลังซ่อม -->
        <div v-show="(activeStep === 'in_progress' || activeStep === 'all') && isStepReached('in_progress')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">3</span>
              {{ text('การดำเนินงานซ่อม', 'Repair in progress') }}
            </h3>
            <span class="text-muted small" v-if="report.technicianPart && (report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted)">
              {{ formatDate(report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted) }}
            </span>
          </div>

          <div class="p-3 rounded bg-light border mb-3">
            <div class="row">
              <div class="col-md-6 mb-2">
                <span class="text-muted small d-block">{{ text('สถานะการดำเนินงาน', 'Progress status') }}</span>
                <strong class="text-dark">{{ report.status === 'in_progress' ? text('ช่างกำลังดำเนินการซ่อมแซม', 'Technician is actively working on the repair') : (['done', 'welldone'].includes(report.status) ? text('ดำเนินการซ่อมเรียบร้อยแล้ว', 'Repair work completed') : text('รอช่างเริ่มดำเนินการ', 'Awaiting repair start')) }}</strong>
              </div>
              <div class="col-md-6 mb-2">
                <span class="text-muted small d-block">{{ text('ระยะเวลาซ่อมโดยประมาณ (ETA)', 'Estimated repair time') }}</span>
                <strong class="text-primary">{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4: ซ่อมเสร็จ รอประเมิน -->
        <div v-show="(activeStep === 'done' || activeStep === 'all') && isStepReached('done')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">4</span>
              {{ text('ผลการซ่อมแซม', 'Repair completion') }}
            </h3>
            <span class="text-muted small" v-if="getCompletionDate()">
              {{ formatDate(getCompletionDate()) }}
            </span>
          </div>

          <div class="mb-4">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('บันทึกผลการซ่อมของช่าง', 'Technician repair notes') }}</h5>
            <p class="sa-description">{{ getMaintenanceDetails() || text('ยังไม่มีการบันทึกผลการซ่อม', 'No repair result recorded yet') }}</p>
          </div>

          <div class="mb-3">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพถ่ายหลังซ่อมเสร็จ', 'After repair photo') }}</h5>
            <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair @open="openImage" @error="handleImageError" />
            <p v-else class="text-muted small">{{ text('ยังไม่มีภาพผลการซ่อม', 'No repair result image yet') }}</p>
          </div>

          <!-- Active User Input: Rating Form right under timeline if status is done -->
      <div
        v-if="report.status === 'done'"
        class="evaluation-box text-center p-4 my-4 rounded"
        style="background: #eff6ff; border: 2px solid #bfdbfe;"
      >
        <h5 class="font-weight-bold text-primary mb-2">
          ⭐ {{ $t('ticketDetail.evalPlease') }}
        </h5>
        <p class="text-muted small mb-3">
          {{ text('ช่างแจ้งซ่อมเสร็จแล้ว กรุณาประเมินความพึงพอใจการบริการด้านล่างนี้', 'The technician has finished repairing. Please rate your satisfaction below.') }}
        </p>

        <div class="star-rating mb-3" role="radiogroup" :aria-label="$t('ticketDetail.evalPlease')">
          <span
            v-for="star in 5"
            :key="star"
            class="star" v-keyboard-activate role="radio" :aria-checked="String(rating === star)" :tabindex="star === (rating || 1) ? 0 : -1" :aria-label="text(star + ' จาก 5 ดาว', star + ' out of 5 stars')"
            :class="{
              filled: star <= (hoverRating || rating),
              disabled: false,
            }"
            @mouseover="onStarHover(star)"
            @mouseleave="onStarLeave()"
            @click="onStarClick(star)"
            style="font-size: 36px; cursor: pointer; color: #cbd5e1; transition: color 0.15s; margin: 0 4px;"
          >★</span>
        </div>

        <CButton
          color="primary"
          size="lg"
          @click="submitEvaluation"
          :disabled="isSubmitting"
          class="px-5 shadow-sm font-weight-bold"
          style="background-color: #1e40af; border-color: #1e40af;"
        >
          <span v-if="!isSubmitting">{{ $t('ticketDetail.evalSubmit') }}</span>
          <span v-else>{{ $t('ticketDetail.saving') }}</span>
        </CButton>
      </div>
        </div>

        <!-- Step 5: ประเมินแล้ว -->
        <div v-show="(activeStep === 'welldone' || activeStep === 'all') && isStepReached('welldone')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">5</span>
              {{ text('ผลการประเมินความพึงพอใจ', 'Service evaluation') }}
            </h3>
            <span class="text-muted small" v-if="report.rating && report.rating.evaluatedAt">
              {{ formatDate(report.rating.evaluatedAt) }}
            </span>
          </div>

          <div v-if="report.rating && report.rating.stars">
            <!-- 1. Star Rating Banner -->
            <div class="text-center p-3 rounded bg-light border mb-4">
              <div class="sa-rating text-warning font-weight-bold mb-1" style="font-size: 1.8rem;">
                ★ {{ report.rating.stars }} / 5
              </div>
              <p class="text-muted mb-0 small">
                {{ text('ประเมินเมื่อ', 'Rated at') }} {{ formatDate(report.rating.evaluatedAt) }}
              </p>
              <p v-if="report.rating.comment || report.rating.feedback" class="mt-2 text-dark font-italic mb-0">
                "{{ report.rating.comment || report.rating.feedback }}"
              </p>
            </div>

            <!-- 2. Side-by-side Image Comparison (รูปภาพก่อนซ่อม vs รูปภาพหลังซ่อม) -->
            <div class="mb-4">
              <h5 class="font-weight-bold text-dark mb-3" style="font-size: 1rem;">
                <CIcon name="cil-image" class="mr-1 text-primary" />
                {{ text('เปรียบเทียบรูปภาพก่อน-หลังซ่อม', 'Before & After Repair Photos') }}
              </h5>
              <div class="row">
                <!-- Left: Before Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพก่อนซ่อม', 'Before Repair Photo') }}</span>
                    </div>
                    <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพก่อนซ่อม', 'No before-repair photo') }}
                    </div>
                    <div v-if="getReportInformedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                      <span>{{ text('เวลาที่แจ้ง:', 'Reported:') }} {{ formatDate(getReportInformedDate()) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Right: After Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพหลังซ่อม', 'After Repair Photo') }}</span>

                    </div>
                    <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพหลังซ่อม', 'No after-repair photo') }}
                    </div>
                    <div v-if="getWorkFinishedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-check-circle" size="sm" class="mr-1 text-success" />
                      <span>{{ text('เวลาที่ซ่อมเสร็จ:', 'Completed:') }} {{ formatDate(getWorkFinishedDate()) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Details Grid: Technician Info -->
            <div class="row">
              <!-- Technician Info -->
              <div class="col-12 mb-3">
                <div class="border rounded bg-white p-3 h-100 shadow-sm">
                  <div class="font-weight-bold text-dark border-bottom pb-2 mb-2">
                    <CIcon name="cil-user" class="mr-1 text-primary" />
                    {{ text('ข้อมูลช่างที่ซ่อม', 'Assigned Technician') }}
                  </div>
                  <div class="d-flex align-items-center pt-1">
                    <CIcon name="cil-wrench" class="mr-2 text-primary" size="lg" />
                    <div>
                      <strong class="text-dark d-block">
                        {{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}
                      </strong>
                      <small class="text-muted" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
                        {{ text('ซ่อมเสร็จเมื่อ', 'Completed at') }}: {{ formatDate(report.technicianPart.dateTimeFinished) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-muted text-center py-3">
            {{ report.status === 'done' ? text('อยู่ระหว่างรอการประเมินความพึงพอใจ', 'Awaiting rating') : text('ยังไม่มีข้อมูลการประเมิน', 'No evaluation data yet') }}
          </div>
        </div>
      </div>
    </section>

    <!-- Modal for Image Preview -->
    <CModal :aria-label="$t('sendReport.imageLabel')" :show.sync="imageModal" centered size="lg">
      <img :src="selectedImageUrl" class="img-fluid w-100" />
      <template #footer>
        <CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') || text('ปิด', 'Close') }}</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ProblemPhotoGallery from "@/components/common/ProblemPhotoGallery.vue";
import axios from "axios";
import Service from "@/service/api";
import Swal from "sweetalert2";
import reportDetailMixin from "@/i18n/reportDetailMixin";
import TicketDetailSummary from "@/components/common/TicketDetailSummary.vue";
import {
  getTicketAssetCode,
  getTicketProblemDetails,
  getTicketProblemImage,
  getTicketMaintenanceImage,
  getTicketMaintenanceImages,
  getTicketMaintenanceDetails,
  isTicketOutdoor,
  getTicketLocationFull,
  getTicketAvailableTime,
  formatTicketDate,
  getTicketUserName,
  getTicketPersonalPhone,
  getTicketOfficePhone,
  getTicketReporterAffiliation,
  getTicketSectionName,
  getTicketJobTypeName,
  getTicketOnBehalfOf,
  getTicketTechnicianPhone,
} from "@/utils/ticketViewHelper";

export default {
  directives: { keyboardActivate },
  name: "UReportDetail",
  components: { CModal, ProblemPhotoGallery, TicketDetailSummary },
  mixins: [reportDetailMixin],
  props: { report: { type: Object, required: true } },
  data: () => ({
    activeStep: 'assigned',
    imageModal: false,
    selectedImageUrl: null,
    activeAfterPhotoIdx: 0,
    rating: 5,
    hoverRating: 0,
    isSubmitting: false,
    map: null,
    marker: null,
  }),
  created() {

    if (this.report?.status === "welldone" && this.report.rating) {
      this.rating = this.report.rating.stars;
    }
  },
  computed: {
    lang() {
      return this.$store.state.locale.lang || 'th';
    },
    overdue() {
      const isOpen = ['pending', 'assigned', 'in_progress'].includes(this.report?.status);
      return isOpen && Boolean(this.report?.slaDeadline) && new Date(this.report.slaDeadline).getTime() < Date.now();
    },
    hasEtaDisplay() {
      const eta = this.report?.estimatedRepairTime;
      return !!(eta && eta.optionCode && eta.optionCode !== 'none');
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude)
        };
      }
      return { lat: 0, lng: 0 };
    },
    nextStep() {
      const next = {
        pending: ['รอหน่วยงานรับเรื่องและมอบหมายช่าง', 'Awaiting assignment by the maintenance section'],
        assigned: ['รอช่างรับงานและเริ่มซ่อม', 'Awaiting technician acceptance and repair'],
        in_progress: ['ช่างกำลังดำเนินการ รอบันทึกผลการซ่อม', 'Repair is in progress; awaiting completion'],
        done: ['ช่างแจ้งซ่อมเสร็จแล้ว รอประเมินบริการ', 'Technician reported completion; awaiting rating'],
        welldone: ['มีผลการซ่อมและการประเมินบริการแล้ว', 'Repair result and service rating are available'],
        cancelled: ['สิ้นสุดการดำเนินการสำหรับรายการนี้', 'This request is no longer active'],
      };
      return (next[this.report?.status] || ['', ''])[this.lang === 'en' ? 1 : 0];
    },
    timeline() {
      const statuses = ['pending', 'assigned', 'in_progress', 'done', 'welldone'];
      const dates = [
        this.getReportInformedDate(),
        this.getSupervisorInformedDate(),
        this.getWorkStartedDate(),
        this.getWorkFinishedDate(),
        this.report && this.report.rating && this.report.rating.evaluatedAt
      ];
      const currentIdx = statuses.indexOf(this.report && this.report.status);
      return statuses.map((status, index) => {
        let reached = false;
        if (index === 0 || index === 1) {
          reached = true;
        } else if (this.isPostponedActive) {
          reached = false;
        } else {
          reached = Boolean(dates[index]) || (currentIdx !== -1 && index <= currentIdx);
        }
        return {
          status,
          label: index === 0
            ? this.text('แจ้งเรื่อง', 'Reported')
            : (index === 1
              ? this.text('มอบหมายช่าง', 'Assign Technician')
              : this.statusLabel(status)),
          date: (this.isPostponedActive && index >= 2) ? null : dates[index],
          reached
        };
      });
    }
  },
  methods: {
    text(th, en) {
      return this.lang === 'en' ? en : th;
    },
    statusLabel(status) {
      const map = {
        pending: ['รอรับเรื่อง', 'Pending'],
        assigned: ['มอบหมายช่าง', 'Assign Technician'],
        in_progress: ['กำลังซ่อม', 'In progress'],
        done: ['ซ่อมเสร็จ รอประเมิน', 'Repaired · awaiting rating'],
        welldone: ['ประเมินแล้ว', 'Rated'],
        cancelled: ['ยกเลิก', 'Cancelled'],
      };
      return (map[status] || [status, status])[this.lang === 'en' ? 1 : 0];
    },
    canCancelTicket(report = this.report) {
      if (!report) return false;
      if (['done', 'welldone', 'cancelled', 'in_progress'].includes(report.status)) {
        return false;
      }
      const assignments = report.supervisorPart?.assignments || [];
      const hasAcceptedAssignment = assignments.some(a => a.status === 'accepted');
      const hasAcceptedDate = Boolean(report.technicianPart?.dateTimeAccepted);
      if (hasAcceptedAssignment || hasAcceptedDate) {
        return false;
      }
      return ['pending', 'assigned'].includes(report.status);
    },
    async cancelTicket() {
      const result = await Swal.fire({
        title: this.$t('table.user.swalCancelTitle'),
        html: this.$t('table.user.swalCancelHtml'),
        input: 'textarea',
        inputPlaceholder: this.$t('table.user.swalPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('table.user.swalPlaceholder')
        },
        showCancelButton: true,
        confirmButtonText: this.$t('table.user.btnConfirmCancel'),
        cancelButtonText: this.$t('table.user.btnBack'),
        cancelButtonColor: '#dc2626',
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return this.$t('table.user.swalReasonRequired');
          }
        }
      });

      if (result.isConfirmed) {
        try {
          const userId = this.report?.userPart?.user?._id || this.report?.userPart?.user;
          const response = await Service.ticket('put', {
            userId: userId,
            cancelReason: result.value,
          }, { urlAppend: `/${this.report._id}/cancel` });
          const data = response.data;

          if (data.success) {
            Swal.fire({
              icon: 'success',
              title: this.$t('table.user.swalSuccess'),
              text: this.$t('table.user.swalSuccessText'),
              showConfirmButton: false,
              timer: 1600,
              timerProgressBar: true,
              allowOutsideClick: false
            });
            this.$emit('refresh');
            this.$emit('close');
          } else {
            Swal.fire('Error', data.message || this.$t('table.user.swalErrCancel'), 'error');
          }
        } catch (error) {
          console.error("Cancel error:", error);
          Swal.fire('Error', this.$t('table.user.swalErrNetwork'), 'error');
        }
      }
    },
    async submitEvaluation() {
      this.isSubmitting = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/rating`,
          { stars: this.rating }
        );
        await Swal.fire({
          icon: "success",
          title: this.$t('ticketDetail.swalEvalOk'),
          confirmButtonColor: "#1e40af",
          timer: 1600,
          showConfirmButton: false,
        });
        this.$emit("refresh");
        this.$emit("close");
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmitting = false;
      }
    },
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.split("/").pop();
      return `${process.env.VUE_APP_APP_URL}/uploads/${filename}`;
    },
    openImage(img) {
      this.selectedImageUrl = this.getImageUrl(img);
      this.imageModal = true;
    },
    handleImageError(e) { console.error("Image failed to load"); },

    getUserName() {
      return getTicketUserName(this.report);
    },
    getOnBehalfOf() {
      return getTicketOnBehalfOf(this.report);
    },
    getPersonalPhone() { return getTicketPersonalPhone(this.report); },
    getOfficePhone() { return getTicketOfficePhone(this.report); },
    getTechnicianPhone() {
      return getTicketTechnicianPhone(this.report, this.technicians || this.techniciansList, this.profile);
    },
    getAvailableTime() {
      return getTicketAvailableTime(this.report, this.lang, this.$t.bind(this));
    },
    getReporterAffiliation() { return getTicketReporterAffiliation(this.report); },
    getSectionName() { return getTicketSectionName(this.report, this.lang); },
    getDepartment() { return getTicketSectionName(this.report, this.lang); },
    getCategoryName() { return getTicketJobTypeName(this.report, this.lang); },
    getLocationFull() {
      return getTicketLocationFull(this.report, this.$t.bind(this), this.$store.state.locale.lang);
    },
    getAssetCode() { return getTicketAssetCode(this.report); },
    getProblemDetails() { return getTicketProblemDetails(this.report); },
    getProblemImage() { return getTicketProblemImage(this.report); },
    getMaintenanceImage() { return getTicketMaintenanceImage(this.report); },
    getMaintenanceImages() { return getTicketMaintenanceImages(this.report); },
    getActiveMaintenanceImage() {
      const images = this.getMaintenanceImages();
      if (!images || images.length === 0) return this.getMaintenanceImage();
      return images[this.activeAfterPhotoIdx] || images[0];
    },
    getMaintenanceDetails() { return getTicketMaintenanceDetails(this.report); },
    getCompletionDate() { return this.report.technicianPart?.dateTimeFinished; },
    onStarHover(star) {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = star;
      }
    },
    onStarLeave() {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = 0;
      }
    },
    onStarClick(star) {
      if (this.report && this.report.status === 'done') {
        this.rating = star;
      }
    },
    getTechnicianName() {
      const assignments = Array.isArray(this.report?.supervisorPart?.assignments)
        ? this.report.supervisorPart.assignments
        : [];
      if (assignments.length > 0) {
        const accepted = assignments.filter(a => a && a.status === 'accepted');
        const active = assignments.filter(a => a && a.status !== 'rejected');
        const listToUse = accepted.length > 0 ? accepted : (active.length > 0 ? active : assignments);

        const techList = this.technicians || this.techniciansList || [];
        const names = listToUse
          .map(a => {
            const assignee = a?.assignee;
            if (assignee && typeof assignee === 'object') {
              const n = `${assignee.name || assignee.firstname || ''} ${assignee.surname || assignee.lastname || ''}`.trim();
              if (n) return n;
              if (assignee.username) return assignee.username;
            }
            if (typeof assignee === 'string' || typeof assignee === 'number') {
              const strId = String(assignee);
              if (this.profile && String(this.profile._id || this.profile.id) === strId) {
                const n = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
                if (n) return n;
                if (this.profile.username) return this.profile.username;
              }
              if (Array.isArray(techList)) {
                const found = techList.find(t => String(t._id || t.id) === strId);
                if (found) {
                  const n = `${found.name || ''} ${found.surname || ''}`.trim();
                  if (n) return n;
                  if (found.username) return found.username;
                }
              }
            }
            return null;
          })
          .filter(Boolean);

        if (names.length > 0) {
          return names.join(', ');
        }
      }

      const tech = this.report?.supervisorPart?.assignedTechnician;
      if (tech) {
        if (typeof tech === 'object') {
          const n = `${tech.name || ''} ${tech.surname || ''}`.trim();
          if (n) return n;
          if (tech.username) return tech.username;
        } else if (typeof tech === 'string' || typeof tech === 'number') {
          const strId = String(tech);
          if (this.profile && String(this.profile._id || this.profile.id) === strId) {
            const n = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
            if (n) return n;
          }
          const techList = this.technicians || this.techniciansList || [];
          if (Array.isArray(techList)) {
            const found = techList.find(t => String(t._id || t.id) === strId);
            if (found) {
              const n = `${found.name || ''} ${found.surname || ''}`.trim();
              if (n) return n;
            }
          }
        }
      }

      const tpTech = this.report?.technicianPart?.technician;
      if (tpTech && typeof tpTech === 'object') {
        const n = `${tpTech.name || ''} ${tpTech.surname || ''}`.trim();
        if (n) return n;
      }

      if (this.report?.status === 'assigned') {
        return this.$t ? this.$t('ticketDetail.techPending') : 'รอยืนยันการรับงาน';
      }

      return this.$t ? this.$t('ticketDetail.techNotYet') : 'ยังไม่มอบหมายช่าง';
    },
    formatDate(dateString) {
      return formatTicketDate(dateString, this.lang);
    },
    isOutdoor() {
      return isTicketOutdoor(this.report);
    },

  },
};
</script>

<style scoped>
.star.filled {
  color: #f59e0b !important;
}
.star.disabled {
  cursor: default !important;
}
</style>
